import { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';

export interface PlaceResult {
  address: string;
  city: string;
  postcode: string;
  country: string;
  lat?: number;
  lon?: number;
  formatted: string;
}

interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
}

interface PlacesAutocompleteProps {
  label?: string;
  placeholder?: string;
  value?: PlaceResult | null;
  onChange: (place: PlaceResult | null) => void;
  error?: boolean;
  helperText?: string;
}

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

// Generate a session token for billing optimization
function generateSessionToken(): string {
  return crypto.randomUUID();
}

export default function PlacesAutocomplete({
  label = 'Omlæsningsadresse',
  placeholder = 'Søg efter adresse...',
  value,
  onChange,
  error,
  helperText,
}: PlacesAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value?.formatted || '');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const sessionTokenRef = useRef<string>(generateSessionToken());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch autocomplete suggestions using REST API
  const fetchSuggestions = useCallback(async (input: string) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      return;
    }

    if (!GOOGLE_API_KEY) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        'https://places.googleapis.com/v1/places:autocomplete',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': GOOGLE_API_KEY,
            'X-Goog-FieldMask':
              'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat',
          },
          body: JSON.stringify({
            input,
            sessionToken: sessionTokenRef.current,
            includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route', 'establishment'],
            includedRegionCodes: ['dk', 'se', 'no', 'de'],
            languageCode: 'da',
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      const formattedSuggestions: PlaceSuggestion[] =
        data.suggestions?.map((s: { placePrediction?: {
          placeId?: string;
          text?: { text?: string };
          structuredFormat?: {
            mainText?: { text?: string };
            secondaryText?: { text?: string };
          };
        }}) => ({
          placeId: s.placePrediction?.placeId || '',
          mainText: s.placePrediction?.structuredFormat?.mainText?.text || '',
          secondaryText: s.placePrediction?.structuredFormat?.secondaryText?.text || '',
          fullText: s.placePrediction?.text?.text || '',
        })) || [];

      setSuggestions(formattedSuggestions);
      setIsOpen(formattedSuggestions.length > 0);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, fetchSuggestions]);

  // Get place details when user selects a suggestion
  const getPlaceDetails = async (placeId: string): Promise<PlaceResult | null> => {
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          headers: {
            'X-Goog-Api-Key': GOOGLE_API_KEY,
            'X-Goog-FieldMask': 'id,formattedAddress,addressComponents,location',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const place = await response.json();
      const components = place.addressComponents || [];

      const getComponent = (type: string): { long: string; short: string } => {
        const component = components.find((c: { types: string[]; longText?: string; shortText?: string }) =>
          c.types.includes(type)
        );
        return {
          long: component?.longText || '',
          short: component?.shortText || '',
        };
      };

      const streetNumber = getComponent('street_number').long;
      const route = getComponent('route').long;
      const address = streetNumber ? `${route} ${streetNumber}` : route;

      const result: PlaceResult = {
        address,
        city: getComponent('locality').long || getComponent('postal_town').long,
        postcode: getComponent('postal_code').long,
        country: getComponent('country').short,
        lat: place.location?.latitude,
        lon: place.location?.longitude,
        formatted: place.formattedAddress || '',
      };

      // Reset session token after selection (for billing optimization)
      sessionTokenRef.current = generateSessionToken();

      return result;
    } catch (err) {
      console.error('Failed to fetch place details:', err);
      return null;
    }
  };

  // Handle suggestion selection
  const handleSelect = async (suggestion: PlaceSuggestion) => {
    setInputValue(suggestion.fullText);
    setIsOpen(false);
    setSuggestions([]);
    setIsLoading(true);

    const details = await getPlaceDetails(suggestion.placeId);
    setIsLoading(false);

    if (details) {
      onChange(details);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSuggestions([]);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
      )}
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (!e.target.value) {
            onChange(null);
          }
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        error={error}
        helperText={helperText}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
        autoComplete="off"
      />

      {isOpen && suggestions.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 300,
            overflow: 'auto',
            mt: 0.5,
          }}
        >
          <List dense disablePadding>
            {suggestions.map((suggestion, index) => (
              <ListItemButton
                key={suggestion.placeId}
                selected={index === highlightedIndex}
                onClick={() => handleSelect(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
                sx={{
                  py: 1,
                  borderBottom: index < suggestions.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <LocationOnIcon
                  sx={{ mr: 1.5, color: 'text.secondary' }}
                  fontSize="small"
                />
                <ListItemText
                  primary={suggestion.mainText}
                  secondary={suggestion.secondaryText}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'text.secondary',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
